import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Input, Spinner } from '@easydent/shared-ui';
import {
  listSuppliers, createSupplier, setSupplierStatus, fundSupplier, type SupplierRow,
} from '@/lib/api';

const money = (n: number, c: string) => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(n); }
  catch { return `${n.toFixed(2)} ${c}`; }
};

const SuppliersPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SupplierRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', contactName: '', password: '' });
  const [busy, setBusy] = useState(false);

  const load = () => listSuppliers().then(setRows).catch(() => setError(t('suppliers.loadError', "Could not load suppliers.")));
  useEffect(() => { load(); }, []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await createSupplier({ name: form.name, email: form.email, contactName: form.contactName || undefined, password: form.password });
      setForm({ name: '', email: '', contactName: '', password: '' });
      setCreating(false);
      await load();
    } catch (err) {
      setError((err as { response?: { data?: { title?: string } } })?.response?.data?.title ?? t('suppliers.createError', "Could not create supplier."));
    } finally { setBusy(false); }
  };

  const fund = async (s: SupplierRow) => {
    const v = window.prompt(`Top up ${s.name}'s wallet (${s.currency}):`, '50');
    const amount = Number(v);
    if (!v || !Number.isFinite(amount) || amount <= 0) return;
    await fundSupplier(s.id, amount); await load();
  };

  const toggle = async (s: SupplierRow) => {
    await setSupplierStatus(s.id, s.status === 'Active' ? 'Suspended' : 'Active'); await load();
  };

  if (error && !rows) return <Alert variant="error">{error}</Alert>;
  if (!rows) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-body">{t('suppliers.title', "Suppliers")}</h1>
        <Button onClick={() => setCreating((v) => !v)}>{creating ? 'Cancel' : t('suppliers.new', 'New supplier')}</Button>
      </div>
      {error && <Alert variant="error">{error}</Alert>}

      {creating && (
        <Card>
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-2">
            <Input placeholder={t('suppliers.companyName', "Company name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Input type="email" placeholder={t('suppliers.loginEmail', "Login email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <Input placeholder={t('suppliers.contactName', "Contact name (optional)")} value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
            <Input type="password" placeholder={t('suppliers.initialPassword', "Initial password (min 10)")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <div className="sm:col-span-2"><Button loading={busy} disabled={!form.name || !form.email || form.password.length < 10}>{t('suppliers.create', "Create supplier")}</Button></div>
          </form>
        </Card>
      )}

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-soft text-left text-xs uppercase tracking-wide text-subtle-ink">
              <th className="px-2 py-2">{t('suppliers.supplier', "Supplier")}</th><th className="px-2 py-2">{t('suppliers.statusHeader', 'Status')}</th>
              <th className="px-2 py-2">{t('suppliers.wallet', "Wallet")}</th><th className="px-2 py-2">{t('suppliers.pending', "Pending")}</th><th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-line-faint">
                <td className="px-2 py-2"><div className="font-medium text-body">{s.name}</div><div className="text-xs text-subtle-ink">{s.email}</div></td>
                <td className="px-2 py-2"><span className={`rounded px-2 py-0.5 text-xs ${s.status === 'Active' ? 'bg-brand-50 text-brand-ink-strong' : 'bg-red-50 text-red-600'}`}>{s.status}</span></td>
                <td className="px-2 py-2">{money(s.walletBalance, s.currency)}</td>
                <td className="px-2 py-2">{s.pendingCreatives > 0 ? <span className="rounded-full bg-amber-100 px-2 text-xs text-amber-700">{s.pendingCreatives}</span> : '—'}</td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => fund(s)} className="mr-3 text-brand-ink hover:underline">{t('suppliers.fund', "Fund")}</button>
                  <button onClick={() => toggle(s)} className="text-muted hover:underline">{s.status === 'Active' ? 'Suspend' : 'Activate'}</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-subtle-ink">{t('suppliers.empty', "No suppliers yet.")}</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default SuppliersPage;
